import {
  getCommonReducer,
  setGenres,
  setGenresError,
  setGenresLoading,
} from '@store/reducers/commonStore';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getGenres } from '../services/api';
import { useAppDispatch } from '../store/store';

// Global flags to prevent multiple simultaneous API calls per category
const isGenresFetching: { [key: string]: boolean } = {};
const genresFetchPromise: { [key: string]: Promise<void> | null } = {};

/**
 * Custom hook for centralized genre management
 * Fetches genres based on category and stores them in Redux store
 * All components can use this hook to access genres without multiple API calls
 * @param category - The category to fetch genres for (music, serenity, book)
 * @param skip - If true, skip fetching genres (useful when genres are passed as props)
 */
export const useGenres = (
  category: string = 'music',
  skip: boolean = false,
) => {
  const dispatch = useAppDispatch();
  const hasFetchedRef = useRef<{ [key: string]: boolean }>({});

  // Get genres state from Redux store
  const { genresByCategory, genresLoading, genresError } =
    useSelector(getCommonReducer);

  // Get category-specific data
  const validCategory: 'music' | 'serenity' | 'book' | 'indian-music' = [
    'music',
    'serenity',
    'book',
    'indian-music',
    'Indian music',
  ].includes(category)
    ? category === 'Indian music'
      ? 'indian-music'
      : (category as 'music' | 'serenity' | 'book' | 'indian-music')
    : 'music';
  const genres = genresByCategory[
    validCategory as keyof typeof genresByCategory
  ] || ['All'];
  const loading =
    genresLoading[validCategory as keyof typeof genresLoading] || false;
  const error = genresError[validCategory as keyof typeof genresError] || null;

  // Fetch genres only once per category
  useEffect(() => {
    // Skip fetching if skip flag is true (e.g., when genres are passed as props on home page)
    if (skip) {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          `useGenres: Skipping fetch for category: ${validCategory} (genres passed as props)`,
        );
      }
      return;
    }

    // Debug logging for genre fetching
    if (process.env.NODE_ENV === 'development') {
      console.log(
        `useGenres: Checking if should fetch for category: ${validCategory}`,
        {
          genresLength: genres.length,
          loading,
          error,
          isGenresFetching: isGenresFetching[validCategory],
          hasFetched: hasFetchedRef.current[validCategory],
        },
      );
    }

    const shouldFetchGenres =
      genres.length <= 1 &&
      !loading &&
      !error &&
      !isGenresFetching[validCategory] &&
      !hasFetchedRef.current[validCategory];

    if (shouldFetchGenres) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`useGenres: Starting fetch for category: ${validCategory}`);
      }
      hasFetchedRef.current[validCategory] = true;
      isGenresFetching[validCategory] = true;

      const fetchGenres = async () => {
        console.log(
          'useGenres: Fetching genres from API for category:',
          category,
        );
        dispatch(setGenresLoading({ category: validCategory, loading: true }));

        try {
          const apiCategory =
            validCategory === 'indian-music' ? 'indian music' : validCategory;
          const genreList = await getGenres(apiCategory);
          console.log(
            'useGenres: Successfully fetched genres for category:',
            validCategory,
            genreList,
          );
          dispatch(setGenres({ category: validCategory, genres: genreList }));
        } catch (error) {
          console.error('useGenres: Error fetching genres:', error);
          dispatch(
            setGenresError({
              category: validCategory,
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to fetch genres',
            }),
          );
          hasFetchedRef.current[validCategory] = false; // Allow retry on error
        } finally {
          isGenresFetching[validCategory] = false;
        }
      };

      // Store the promise to prevent duplicate calls for this category
      if (!genresFetchPromise[validCategory]) {
        genresFetchPromise[validCategory] = fetchGenres();
      }
    }
  }, [category, validCategory, genres.length, loading, error, dispatch, skip]); // Re-fetch when category changes or skip flag changes

  // Function to manually refresh genres if needed
  const refreshGenres = async () => {
    console.log(
      'useGenres: Manually refreshing genres for category:',
      validCategory,
    );
    dispatch(setGenresLoading({ category: validCategory, loading: true }));

    try {
      const apiCategory =
        validCategory === 'indian-music' ? 'Indian music' : validCategory;
      const genreList = await getGenres(apiCategory);
      dispatch(setGenres({ category: validCategory, genres: genreList }));
    } catch (error) {
      console.error('useGenres: Error refreshing genres:', error);
      dispatch(
        setGenresError({
          category: validCategory,
          error:
            error instanceof Error ? error.message : 'Failed to refresh genres',
        }),
      );
    }
  };

  return {
    genres,
    loading,
    error,
    refreshGenres,
  };
};
